import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore";
import { useReviewFilterStore } from "../stores/useReviewFilterStore";
import {
  buildReviewSearchParams,
  parseReviewSearchParams,
} from "../utils/review-url-state";

/**
 * Keeps the review session in the address bar.
 *
 * Conflict rule — the URL wins on first load, the stores win afterwards.
 * A pasted link has to mean the same thing to the person who receives it as
 * it did to the person who sent it, so on mount an incoming URL overwrites
 * the persisted filters and the global time range. Once the page is running,
 * the reviewer's own chip commits are authoritative and the URL just follows
 * them.
 *
 * Writes use replace, not push: a chip commit is not a navigation, and
 * pushing would bury the previous page under one history entry per filter
 * tweak.
 */
export const useReviewUrlSync = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedLocationId = useReviewFilterStore((s) => s.selectedLocationId);
  const selectedMachineIds = useReviewFilterStore((s) => s.selectedMachineIds);
  const appliedFilters = useReviewFilterStore((s) => s.appliedFilters);
  const dateRange = useDateTimeRangeStore((s) => s.value);

  // Hydration must happen exactly once, before the first write-back — otherwise
  // the write effect would immediately overwrite the incoming link with the
  // stale persisted state.
  const hydrated = useRef(false);

  useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;

    const incoming = parseReviewSearchParams(searchParams);
    if (!incoming) return;

    const filterStore = useReviewFilterStore.getState();
    filterStore.setSelectedLocationId(incoming.locationId);
    filterStore.setSelectedMachineIds(incoming.machineIds);
    filterStore.setAppliedFilters({
      // `limit` is the page size, not part of the shared view — keep whatever
      // the store already had rather than dropping to the backend default.
      limit: filterStore.appliedFilters.limit,
      ...incoming.filters,
    });

    if (incoming.fromDate && incoming.toDate) {
      useDateTimeRangeStore.getState().setValue({
        fromDate: new Date(incoming.fromDate),
        toDate: new Date(incoming.toDate),
      });
    }
    // Deliberately mount-only: this is the "URL wins on first load" half of
    // the conflict rule, and re-running it would fight the reviewer's edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hydrated.current) return;

    const next = buildReviewSearchParams({
      locationId: selectedLocationId,
      machineIds: selectedMachineIds,
      fromDate: dateRange.fromDate.toISOString(),
      toDate: dateRange.toDate.toISOString(),
      filters: appliedFilters,
    });

    // Skip no-op writes so an unrelated re-render doesn't churn history state.
    if (next.toString() === searchParams.toString()) return;
    setSearchParams(next, { replace: true });
  }, [
    selectedLocationId,
    selectedMachineIds,
    appliedFilters,
    dateRange,
    searchParams,
    setSearchParams,
  ]);
};
